// lookupFaces — ONE face per entity for every surface that picks it (ADR-078 §6;
// judged in the Workbench, 2026-09-29). A row and a tile are two shapes of one
// set of words: line 1 names the thing as a person would say it, line 2 its
// code and where it sits (a person: the role), a badge speaks only when the
// status is not the ordinary one. A badge's word is drawn ONCE — the badge is
// the eye's copy, hidden from a screen reader (COMPONENTS §Per-primitive WCAG,
// the PickerList row), so the word is handed over as `spoken`: read with the
// row after line 2, never drawn on it (ruled 2026-10-05 — a word drawn twice
// is noise). A row never repeats what the field already says (at a site, the
// site is not printed on every row). Once chosen, the field shows the
// `selectedLabel` — a unit reads "product · reference". A greyed row's reason
// with no badge of its own stays on line 2 ("Not offered").

import { useMemo } from 'react';
import { formatDate } from '../../lib/format.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { SiteLookupRow } from '@ems-app/schemas/sites/lookup';
import type { AssetLookupRow } from '@ems-app/schemas/assets/lookup';
import type { UserLookupRow } from '@ems-app/schemas/users/lookup';
import type { VehicleLookupRow } from '@ems-app/schemas/vehicles/lookup';
import type { AssetModelSearchItem } from '@ems-app/schemas/catalog/responses';
import type { FacedRow } from '../../hooks/useOptions.js';
import { Avatar } from '../ui/Avatar.js';
import { Badge } from '../ui/Badge.js';
import { AssetStatusBadge, CATEGORY_ICON } from './assetsDisplay.js';
import { SiteStatusBadge } from './sitesDisplay.js';

/** A face answers the family's own row shape (`FacedRow` — what `useOptions` turns into an `OptionRow`). */
export type Face<R> = (row: R) => FacedRow;

/** The site: the name · the reference, the cluster and the zone · its status when it is not Active. */
export function useSiteFace(): Face<SiteLookupRow> {
  const { t } = useTranslation('sites');
  return useMemo(
    () => (s) => ({
      value: s.id,
      label: s.name,
      description: [s.reference, s.cluster_name, s.zone_name].filter(Boolean).join(' · '),
      spoken: s.status === 'active' ? undefined : t(`status.${s.status}`),
      trailing: s.status === 'active' ? undefined : <SiteStatusBadge status={s.status} t={t as TFunction} appearance="subtle" />,
      disabled: !s.pickable,
    }),
    [t],
  );
}

/** The vehicle: the plate (line 1 — its real-world identity) · its name, then make and model (line 2) · its status
 *  when it is not Active, else "Stock off" when its hold is switched off (it carries nothing). */
export function useVehicleFace(): Face<VehicleLookupRow> {
  const { t } = useTranslation('common');
  return useMemo(
    () => (v) => {
      const state = v.status !== 'active' ? t(`location.vehicleStatus.${v.status}`) : !v.hold_on ? t('location.holdOff') : null;
      return {
        value: v.id,
        label: v.plate_number,
        description: [v.name, [v.make, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || undefined,
        spoken: state ?? undefined,
        trailing: state ? (
          <Badge variant="neutral" appearance="subtle">
            {state}
          </Badge>
        ) : undefined,
        disabled: !v.pickable,
      };
    },
    [t],
  );
}

/** The unit: the category's glyph · make + product (line 1); the REFERENCE
 *  first (what tells two units of one product apart — the last thing a narrow
 *  panel may cut), then its own name · the category · the place only when it is
 *  not where the field stands · its status when it is not In service. Once
 *  chosen: "product · reference". */
export function useAssetFace(): Face<AssetLookupRow> {
  const { t } = useTranslation('assets');
  return useMemo(
    () => (a) => {
      const Glyph = CATEGORY_ICON[a.category];
      const category = t(`category.${a.category}`);
      const product = a.model ? (a.model.manufacturer ? `${a.model.manufacturer} ${a.model.label}` : a.model.label) : null;
      const what = product ?? a.name ?? category;
      // `place` travels only for a unit that is not at the field's own site — the server left it out otherwise.
      const facts = [a.reference, product ? a.name : null, product || a.name ? category : null, a.place];
      return {
        value: a.id,
        label: what,
        selectedLabel: `${what} · ${a.reference}`,
        description: facts.filter(Boolean).join(' · '),
        spoken: a.status === 'deployed' ? undefined : t(`status.${a.status}`),
        icon: <Glyph size={18} />,
        trailing: a.status === 'deployed' ? undefined : <AssetStatusBadge status={a.status} t={t as TFunction} />,
        disabled: !a.pickable,
      };
    },
    [t],
  );
}

/** The product: make + name (line 1 — the catalogue column's own value; no
 *  glyph, every row of one picker is one category); series · part number (line
 *  2 — the codes, typed whole they answer alone), then "Added here" for the
 *  project's own product (the catalogue's word — the badge is its visual echo,
 *  the word is read on line 2) and "Not offered" for a product the catalogue no
 *  longer offers (an edit form shows what it holds, marked — ADR-080 §5; the
 *  search never answers one). The category's GENERIC row says "Generic battery"
 *  and nothing else — no hint, no badge (the rating field that follows says the
 *  rest; never a data-model word on a label). Once chosen: the same line. */
export function useProductFace(): Face<AssetModelSearchItem> {
  const { t } = useTranslation('assets');
  const { t: tc } = useTranslation('catalog');
  return useMemo(
    () => (m) =>
      m.is_generic
        ? { value: m.id, label: t('cell.generic', { category: t(`category.${m.category}`) }) }
        : {
            value: m.id,
            label: [m.manufacturer, m.name].filter(Boolean).join(' '),
            description: [m.series, m.part_number, m.is_disabled ? tc('store.notOffered') : null].filter(Boolean).join(' · ') || undefined,
            spoken: m.is_canonical ? undefined : tc('store.tierLocal'),
            trailing: m.is_canonical ? undefined : (
              <Badge variant="info" appearance="subtle">
                {tc('store.tierLocal')}
              </Badge>
            ),
            disabled: m.is_disabled,
          },
    [t, tc],
  );
}

/** Who answers for a place — the person's face plus "Will be granted scope" on a
 *  pickable row whose scope does not reach the place yet (`covered` false — the
 *  `accountable` rule's row fact, ADR-052 §5): the chip beside the row, its
 *  words read with the row (a pickable row is an active member — no status word
 *  of its own). An ACTIVE member neither covered nor GRANTABLE (ADR-050 — the
 *  viewer's people key grants sight only to its managed roles) comes greyed from
 *  the server: the chip says they cannot see the place, and who grants it first
 *  follows the role on the second line — the role is the reason, and the username
 *  (sent for two people of one name) still tells them apart. A paused (disabled)
 *  account keeps its own status word instead: scope would not make it pickable. */
export function useAccountableFace(opts: { backing?: 'scope' | 'membership' } = {}): Face<UserLookupRow> {
  const person = usePersonFace();
  const { t } = useTranslation('common');
  // At the TEAM level the backing is MEMBERSHIP (ADR-083 §3): the chip says "Will be added to the team", the greyed
  // row "Not on the team" — whoever runs the team adds them first.
  const words = opts.backing === 'membership' ? 'chain.needsMembership' : 'chain.needsScope';
  return useMemo(
    () => (p) => {
      const base = person(p);
      if (p.covered !== false) return base;
      if (p.grantable === false && p.member && p.status === 'active') {
        const chip = t(`${words}.cannot`);
        return {
          ...base,
          description: [base.description, t(`${words}.cannotHelp`)].filter(Boolean).join(' · '),
          spoken: chip,
          trailing: (
            <Badge variant="neutral" appearance="subtle">
              {chip}
            </Badge>
          ),
        };
      }
      if (!p.pickable) return base;
      const chip = t(`${words}.chip`);
      return {
        ...base,
        spoken: chip,
        trailing: (
          <Badge variant="warning" appearance="subtle">
            {chip}
          </Badge>
        ),
      };
    },
    [person, t, words],
  );
}

/** The person: the avatar · the name; the role (a project administrator's word
 *  when they hold no role) — the username ONLY for two people of one name (the
 *  server sends it for them alone) — never a contact; Disabled · Pending ·
 *  Former member as the badge (a former member's status never travels: their
 *  name alone). */
export function usePersonFace(): Face<UserLookupRow> {
  const { t } = useTranslation('users');
  // A former member's leaving date reads in the screen's one zone (ADR-082 §2) — the project's where the picker sits.
  const projectId = useOptionalProjectParams();
  const display = useDisplayZone(projectId ? { kind: 'project', projectId } : { kind: 'person' });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  return useMemo(
    () => (p) => {
      const name = `${p.first_name} ${p.last_name}`.trim();
      // A former member's badge carries the date they left (ADR-081 §7); a line by someone never a member here reads the word alone.
      const left = !p.member && p.left_at ? formatDate(p.left_at, 'date', { timeZone: zone }) : null;
      const state = !p.member ? (left ? t('lookup.formerLeft', { date: left }) : t('lookup.former')) : p.status === 'disabled' ? t('status.disabled') : p.status === 'pending_activation' ? t('lookup.pending') : null;
      const role = p.role_name ?? (p.is_project_admin ? t('lookup.admin') : null);
      return {
        value: p.id,
        label: name,
        description: [role, p.username].filter(Boolean).join(' · ') || undefined,
        spoken: state ?? undefined,
        icon: <Avatar name={name} size="sm" />,
        trailing: state ? <Badge variant="neutral">{state}</Badge> : undefined,
        disabled: !p.pickable,
      };
    },
    [t, zone],
  );
}
